import { useMemo, useState } from 'react';

import { InfoIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { MAX_FETCHED_DOCUMENTS } from '@/api/endpoints';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { DataTable } from '@/components/ui/table/data-table';
import useDatasources from '@/hooks/useDatasources';
import { ROOT_DATASOURCE_NAMES } from '@/modules/collections/constants';
import { useAPIDocuments } from '@/modules/collections/hooks/useAPIDocuments';
import { useDownloadAsZip } from '@/modules/collections/hooks/useDownloadAsZip';
import {
  closeDeleteDocumentsModal,
  openDeleteDocumentsModal,
  setSelectedDocumentsForDeletion,
} from '@/redux/collections/actions';
import { selectSelectedDocumentsForDeletion } from '@/redux/collections/selectors';
import { getPermissions } from '@/redux/permissions/selectors';
import { setDocumentId } from '@/redux/session/actions';
import { selectAccountSlug, selectDocumentId } from '@/redux/session/selectors';
import { openSidebar, setSidebarContent } from '@/redux/sidebar/actions';
import { CollectionDocument, DatasourceStatus, DatasourceType } from '@/shared/types/collections';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { BULK_DELETE_CONCURRENCY, runWithConcurrency } from '@/util/concurrency';

import { columns } from './columns';
import { downloadFaqsAsCsv, getKbRowAction } from '../../../helper';
import { useTabCategory } from '../../../hooks/useCategory';
import { useExternalSourceIssue } from '../../../hooks/useExternalSourceIssue';
import { useFilteredBySearch } from '../../../hooks/useFilteredBySearch';
import { useHashDocumentId } from '../../../hooks/useHashDocumentId';
import { DeleteDocumentsModal } from '../../../modals/DeleteDocumentsModal';
import { MissingArticlesBanner, NoArticlesEmptyState } from '../../ExternalSourceIssue';
import { DocumentsTableToolbar } from '../DocumentsTableToolbar';
import { TableEffects } from '../TableEffects';

export function ContentTable() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { collectionId, datasourceId } = useParams();

  const [search, setSearch] = useState('');
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const { isFAQTab, isDocumentsTab, isWebsiteTab, isExternalTab, category } = useTabCategory();

  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'collections', actions.WRITE)
  );
  const canDelete = useSelector((state: RootState) =>
    getPermissions(state, 'collections', actions.DELETE)
  );
  const canRead = useSelector((state: RootState) =>
    getPermissions(state, 'collections', actions.READ)
  );
  const accountSlug = useSelector(selectAccountSlug);
  const activeDocumentId = useSelector(selectDocumentId);
  const documentsForDeletion = useSelector(selectSelectedDocumentsForDeletion);
  const highlightedDocumentId = useHashDocumentId();

  const {
    datasources: allDatasources,
    listStatus: datasourcesStatus,
    createDatasourceAsync,
  } = useDatasources(collectionId);
  const {
    data: documents,
    isLoading,
    listStatus: documentsStatus,
    updateDocument,
    moveDocument,
    deleteDocumentAsync,
  } = useAPIDocuments({ collection_id: collectionId, datasource_id: datasourceId });
  const { downloadAsZip } = useDownloadAsZip();

  const tableColumns = useMemo(
    () => columns({ updateDocument, moveDocument, createDatasourceAsync, category, canWrite }),
    [updateDocument, moveDocument, createDatasourceAsync, category, canWrite]
  );

  const datasources = useMemo(
    () =>
      allDatasources?.filter(
        (ds) =>
          (isFAQTab &&
            ds.type === DatasourceType.FAQ &&
            ds.name !== ROOT_DATASOURCE_NAMES.faq &&
            ds.datasource_id !== datasourceId) ||
          (isDocumentsTab &&
            ds.type === DatasourceType.FILES &&
            ds.name !== ROOT_DATASOURCE_NAMES.documents &&
            ds.datasource_id !== datasourceId)
      ),
    [allDatasources, datasourceId, isFAQTab, isDocumentsTab]
  );

  // Filtered before the shared provider sees it so the footer count keys off the filtered length.
  const filteredData = useFilteredBySearch(documents, search);

  const datasource = useMemo(
    () => allDatasources?.find((ds) => ds.datasource_id === datasourceId),
    [allDatasources, datasourceId]
  );

  const {
    providerName,
    providerUrl,
    hasEditableCategories,
    hasMissingArticles,
    isCheckingCategories,
  } = useExternalSourceIssue(datasource);

  const isIngesting =
    datasource?.status === DatasourceStatus.PROCESSING ||
    datasource?.pending_count > 0 ||
    datasource?.indexing_count > 0;

  const isEmptyExternalSource =
    !!providerName && documentsStatus === 'success' && !isIngesting && documents.length === 0;
  const showMissingArticlesBanner = !!providerName && hasMissingArticles;
  const hideCreate =
    showMissingArticlesBanner ||
    (isExternalTab && (datasourcesStatus === 'pending' || isCheckingCategories));

  const resetReduxState = () => {
    dispatch(setDocumentId(null));
  };

  const handleRowClick = (item: CollectionDocument) => {
    resetReduxState();

    // Prevent opening the sidebar if the user doesn't have read permissions
    if (!canRead) {
      return;
    }

    if (isFAQTab) {
      dispatch(setDocumentId(item.document_id));
      dispatch(setSidebarContent('add_faq'));
    } else if (isDocumentsTab) {
      dispatch(setDocumentId(item.document_id));
      dispatch(setSidebarContent(item.type === 'txt' ? 'add_txt_document' : 'edit_document'));
    }
  };

  const handleBulkDownload = async (selectedItems: CollectionDocument[]) => {
    if (isFAQTab) {
      await downloadFaqsAsCsv(selectedItems, collectionId, accountSlug);
    } else {
      await downloadAsZip(selectedItems, collectionId, accountSlug);
    }
  };

  const handleMoveToFolder = (
    selectedItems: CollectionDocument[],
    formData: { datasource_id: string }
  ) => {
    selectedItems.forEach((item, index) => {
      // Stagger the moves slightly to avoid overwhelming the browser
      setTimeout(() => {
        moveDocument({
          document_id: item.document_id,
          new_datasource_id: formData.datasource_id,
          datasource_id: datasourceId,
          name: item.name,
        });
      }, index * 100);
    });

    dispatch(setSidebarContent(null));
  };

  const handleUpload = () => {
    resetReduxState();

    if (isFAQTab) {
      dispatch(setSidebarContent('upload_faq'));
    } else if (isDocumentsTab) {
      dispatch(setSidebarContent('upload_documents'));
    }
  };

  const handleCreate = () => {
    resetReduxState();

    if (isFAQTab) {
      dispatch(setSidebarContent('add_faq'));
    } else if (isWebsiteTab) {
      dispatch(setSidebarContent('add_webpages'));
    } else if (isExternalTab) {
      dispatch(openSidebar({ content: 'add_external_sources', meta: { mode: 'create' } }));
    } else if (isDocumentsTab) {
      dispatch(setSidebarContent('add_txt_document'));
    }
  };

  const handleBulkDelete = (selectedItems: CollectionDocument[]) => {
    dispatch(setSelectedDocumentsForDeletion(selectedItems));
    dispatch(openDeleteDocumentsModal());
  };

  const handleConfirmDelete = async () => {
    setIsBulkDeleting(true);
    try {
      await runWithConcurrency(documentsForDeletion, BULK_DELETE_CONCURRENCY, (item) =>
        deleteDocumentAsync({ document_id: item.document_id })
      );
      // Close the sidebar after successful deletion
      dispatch(setSidebarContent(null));
    } finally {
      dispatch(closeDeleteDocumentsModal());
      setIsBulkDeleting(false);
    }
  };

  if (isEmptyExternalSource) {
    return (
      <NoArticlesEmptyState
        providerName={providerName}
        providerUrl={providerUrl}
        hasEditableCategories={hasEditableCategories}
      />
    );
  }

  return (
    <>
      {showMissingArticlesBanner && (
        <MissingArticlesBanner providerName={providerName} providerUrl={providerUrl} />
      )}

      <DataTable.Root<CollectionDocument, unknown>
        fill
        className="min-h-0 flex-1"
        columns={tableColumns}
        data={filteredData}
        rowCount={filteredData.length}
        isLoading={isLoading}
        resourceName="files"
        enableRowHover
        getRowId={(row) => row.document_id}
        onRowClick={handleRowClick}
        getRowClickable={(row) => getKbRowAction(row, category) === 'edit'}
        activeRowId={highlightedDocumentId ?? activeDocumentId ?? undefined}
        emptyStateMessage={t('analytics.no_data')}
      >
        <DataTable.Toolbar<CollectionDocument>>
          {(table) => (
            <DocumentsTableToolbar
              table={table}
              search={search}
              onSearchChange={setSearch}
              permissions={{ canRead, canWrite, canDelete }}
              datasources={datasources}
              onBulkDownload={handleBulkDownload}
              onMoveToFolder={handleMoveToFolder}
              onBulkDelete={handleBulkDelete}
              onUpload={handleUpload}
              onCreate={handleCreate}
              hideCreate={hideCreate}
            />
          )}
        </DataTable.Toolbar>

        <TableEffects data={filteredData} />
        <DataTable.Content />
        <DataTable.Pagination
          centerContent={
            documents.length >= MAX_FETCHED_DOCUMENTS ? (
              <InfoTooltip
                content={t('collections.documents.limit_reached', { 0: MAX_FETCHED_DOCUMENTS })}
              >
                <InfoIcon className="h-3.5 w-3.5 text-muted-foreground" />
              </InfoTooltip>
            ) : undefined
          }
        />
      </DataTable.Root>

      <DeleteDocumentsModal onConfirm={handleConfirmDelete} isDeleting={isBulkDeleting} />
    </>
  );
}
